import type { ReactNode } from 'react'
import type { Zone } from '../types/zones'
import type { UserContextModel } from './types'
import type { UserContextType } from './UserContext'
import { useEffect, useMemo, useState } from 'react'
import { logger } from '../common/logger'
import { usersApiClient } from './api/usersApiClient'
import { defaultUserContext, UserContext } from './UserContext'
import { useTokenInfo } from './useTokenInfo'

export function UserProvider({ children }: { children: ReactNode }) {
  const [userContext, setUserContext] = useState<UserContextType | null>(null)
  const tokenInfo = useTokenInfo()

  useEffect(() => {
    const fetchUserContext = async () => {
      if (tokenInfo) {
        const userContextModel = await usersApiClient.getUserContext(tokenInfo)
        const newUserContext = {
          id: userContextModel.id,
          canManageUsers: userContextModel.canManageUsers,
          canViewAllZones: userContextModel.canViewAllZones,
          zones: userContextModel.zones,
          selectedZone: preselectZoneIfSingle(userContextModel),
          isLoading: false,
        } as UserContextType
        setUserContext(newUserContext)
      }
    }
    fetchUserContext()
      .catch((error: Error) => {
        logger.logError(error)
        console.error('Error acquiring user context', error)
        setUserContext((prevState) => {
          const newState = {
            ...prevState ?? defaultUserContext,
            isLoading: false,
            errorMessage: error.message,
          }
          return newState
        })
      })
  }, [tokenInfo])

  /*
   * If the user has only one zone, which is the normal behaviour of guides, we preselect it.
   * Otherwise, we do not preselect and leave it for the user to select the working destination.
   */
  function preselectZoneIfSingle(userContextModel: UserContextModel | null): Zone | null {
    if (userContextModel?.zones == null || userContextModel.zones.length !== 1) {
      return null
    }

    return userContextModel.zones[0]
  }

  const contextValue = useMemo<UserContextType>(
    () => ({
      id: userContext?.id,
      canManageUsers: userContext?.canManageUsers ?? false,
      canViewAllZones: userContext?.canViewAllZones ?? false,
      zones: userContext?.zones ?? [],
      selectedZone: userContext?.selectedZone ?? null,
      isLoading: userContext?.isLoading ?? true,
      errorMessage: userContext?.errorMessage ?? null,
    }),
    [userContext],
  )

  return (
    <UserContext value={contextValue}>
      {children}
    </UserContext>
  )
}
